import React, { ReactElement, useEffect, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useMediaQuery, BreakPoint } from '../../hooks/useMediaQuery';
import TableCell from './TableCell/TableCell';
import TableHeaderCell, { ScopeType } from './TableHeaderCell/TableHeaderCell';
import styles from './Table.module.scss';
import classes from 'classnames/bind';
import { ITableProps } from './types';

const cx = classes.bind(styles);

const Table = ({
  data,
  placeholderImage,
  verticalAlign = 'top',
  selectedRowId,
  onRowClick,
  colWidths,
  variant = 'regular',
  innerRef,
}: ITableProps): ReactElement => {
  const isMobile = useMediaQuery(BreakPoint.mobile);
  const tableHeaders: string[] = useMemo(() => Object.keys(data[0]).filter((header) => header !== 'id'), [data]);
  const colWidth = useMemo(() => 100 / tableHeaders.length, [tableHeaders]);

  useEffect(() => {
    if (selectedRowId) {
      const element = document.getElementById(selectedRowId);
      if (element) element.scrollIntoView();
    }
  }, [selectedRowId]);

  return (
    <div className={styles['table-container']}>
      {isMobile ? (
        <table className={styles['table-mobile']}>
          {data.map((row: Record<string, string | JSX.Element>) => {
            const rowId = typeof row.id === 'string' ? row.id : ReactDOMServer.renderToStaticMarkup(row.id);
            return (
              <tbody
                key={`row-${row.id}`}
                className={cx(['row-inner-container'], {
                  'is-clickable': onRowClick,
                })}
                id={rowId}
                onClick={onRowClick ? () => onRowClick(rowId) : undefined}
              >
                {tableHeaders.map((header: string) => (
                  <tr key={`${row[header]}-${header}`} className={styles['table-mobile-row']}>
                    <TableHeaderCell header={header} scope={ScopeType.row} />
                    <TableCell row={row} header={header} placeholderImage={placeholderImage} />
                  </tr>
                ))}
              </tbody>
            );
          })}
        </table>
      ) : (
        <table
          className={cx('table', {
            [`table-align-${verticalAlign}`]: verticalAlign,
          })}
        >
          <thead>
            <tr>
              {tableHeaders.map((header: string) => (
                <TableHeaderCell
                  key={header}
                  header={header}
                  scope={ScopeType.col}
                  colWidth={colWidths ? colWidths[header] : colWidth}
                  rowHeight={variant}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row: Record<string, string | JSX.Element>, idx: number) => {
              const rowId = typeof row.id === 'string' ? row.id : ReactDOMServer.renderToStaticMarkup(row.id);

              return (
                <tr
                  ref={innerRef}
                  key={`row-${row.id}`}
                  id={rowId}
                  onClick={onRowClick ? () => onRowClick(rowId) : undefined}
                  className={cx(`${idx > 0 ? 'row-inner-container' : 'first-row-inner-container'}`, {
                    'is-clickable': onRowClick,
                    'selected-row': selectedRowId === rowId,
                  })}
                >
                  {tableHeaders.map((header: string) => (
                    <TableCell
                      key={`${row[header]}-${header}`}
                      row={row}
                      header={header}
                      placeholderImage={placeholderImage}
                      colWidth={colWidths ? colWidths[header] : colWidth}
                      rowHeight={variant}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Table;
